/*
 * A server move in the task tray (app/TaskTray.tsx): one task for the whole move, whatever ops it's made of, with
 * its seven steps as one bar, where it's got to as a chip ("Copy · 42% · 18 MB/s"), and a link to its timeline. The
 * bar speaks as a bulk run's does: done green, failed red, the running step filled by the copy's percent or a band
 * sliding along it when there's none. A move in another org (the person's own, api/work.ts) names that org first and
 * links into it.
 */
import { Link } from "react-router";
import { BUSY_STATES, type Move } from "../../api/moves";
import type { WorkOrg } from "../../api/work";
import { useOrgPath } from "../../app/org";
import { cx } from "../../lib/cx";
import { formatDuration, relativeTime } from "../../lib/format";
import { StatusPill } from "../../ui/Badge";
import { ToneIcon } from "../../ui/EnvChip";
import { IconAlertCircle, IconCheckCircle, Spinner } from "../../ui/icons";

/** How long finished work stays under "Done recently" (as api/tasks.ts). */
const RECENT_MS = 10 * 60_000;

/** The org's moves as the tray lists them: running ones, and those that ended or stopped in the last ten minutes. */
export function trayMoves(moves: Move[] | undefined, now: number): { running: Move[]; recent: Move[] } {
  const all = moves ?? [];
  const running = all.filter((m) => BUSY_STATES.includes(m.state));
  const ended = (m: Move) => (m.state === "done" || m.state === "canceled" ? m.finishedAt : m.state === "failed" || (m.state === "ready" && m.error) ? (m.error?.at ?? null) : null);
  const recent = all.filter((m) => !BUSY_STATES.includes(m.state) && !!ended(m) && now - Date.parse(ended(m)!) < RECENT_MS);
  return { running, recent };
}

export function MoveTaskRow({ m, siteName, org, now, onOpen }: { m: Move; siteName: string | null; org?: WorkOrg | null; now: number; onOpen: () => void }) {
  const here = useOrgPath();
  const path = org ? (p: string) => `/o/${org.slug}/${p}` : here;
  const E = m.environment.label;
  const running = BUSY_STATES.includes(m.state);
  const failed = m.state === "failed" || (m.state === "ready" && !!m.error);
  const current = m.timeline.findIndex((s) => s.state === "running");
  const step = current >= 0 ? m.timeline[current]! : null;
  const title = m.state === "done" ? `Moved ${E} to ${m.target?.name ?? "another server"}` : m.state === "canceled" ? `Canceled moving ${E}` : `Move ${E} to ${m.target?.name ?? "another server"}`;
  const pct = m.live?.percent ?? null;
  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <p className={cx("min-w-0 text-[14px] leading-5 font-medium", failed ? "text-bad" : "text-fg")}>
          {failed ? (
            <IconAlertCircle size={14} className="mr-1 mb-[2px] inline-block align-middle" aria-label="Stopped:" />
          ) : running ? (
            <Spinner size={13} className="mr-1.5 mb-[2px] inline-block align-middle text-info" aria-label="Running:" />
          ) : (
            <IconCheckCircle size={14} className="mr-1 mb-[2px] inline-block align-middle text-good" aria-label="Finished:" />
          )}
          {title}
        </p>
        <span className="shrink-0 pt-px text-[12px] leading-5 text-faint tabular-nums">{running ? formatDuration(now - Date.parse(m.createdAt)) : relativeTime(m.finishedAt ?? m.error?.at ?? m.updatedAt, now)}</span>
      </div>
      {siteName ? <p className="truncate text-[12.5px] text-dim">{[org?.name, siteName, E].filter(Boolean).join(" · ")}</p> : null}
      <div role="progressbar" aria-label={`${title}, progress`} aria-valuemin={0} aria-valuemax={m.timeline.length} aria-valuenow={m.timeline.filter((s) => s.state === "done" || s.state === "skipped").length} aria-valuetext={step ? `${step.label}, step ${current + 1} of ${m.timeline.length}` : undefined} className="mt-2 flex h-1.5 gap-0.5 overflow-hidden rounded-full">
        {m.timeline.map((s) => (
          <span key={s.id} className="relative h-full flex-1 overflow-hidden bg-meter-track first:rounded-l-full last:rounded-r-full">
            {s.state === "running" ? (
              pct !== null && (s.id === "copy" || s.id === "catchup") ? (
                <span className="absolute inset-y-0 left-0 bg-info-fill transition-[width] duration-700" style={{ width: `${Math.min(100, pct)}%` }} />
              ) : (
                <span className="hx-indeterminate absolute inset-0" />
              )
            ) : (
              <span className={cx("absolute inset-0", s.state === "done" || s.state === "skipped" ? "bg-good-fill" : s.state === "failed" ? "bg-bad-fill" : "bg-transparent")} />
            )}
          </span>
        ))}
      </div>
      {/* Where it's got to, a chip or a status line, with the link to its timeline on the same row. */}
      <div className="mt-2 flex items-start gap-3 text-[12.5px]">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {failed ? (
            <p className="flex min-w-0 items-start gap-1.5 py-[3px] leading-[18px]">
              <ToneIcon tone="bad" size={14} className="mt-[2px]" />
              <span className="line-clamp-2 min-w-0 font-medium text-bad">{m.error?.message ?? "It stopped."}</span>
            </p>
          ) : step ? (
            <StatusPill tone="progress" title={m.detail ?? step.detail ?? undefined}>
              <span className="tabular-nums">{[step.label, pct !== null ? `${pct}%` : null, pct !== null ? m.live?.rate : null].filter(Boolean).join(" · ")}</span>
            </StatusPill>
          ) : running ? (
            <StatusPill tone="neutral" icon="clock" title={m.detail ?? undefined}>
              Waiting
            </StatusPill>
          ) : m.state === "done" ? (
            <StatusPill tone="good">Moved</StatusPill>
          ) : m.state === "canceled" ? (
            <StatusPill tone="neutral">{E} stays where it was</StatusPill>
          ) : null}
        </div>
        <Link to={path(`sites/${m.siteId}/env/${m.environment.slug}/move`)} onClick={onOpen} className="shrink-0 leading-6 font-medium text-link hover:underline focus-visible:outline-2 focus-visible:outline-brand-500">
          See the move
        </Link>
      </div>
    </li>
  );
}
